import React, { useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import './StyledSlider.css';


type EffectiveRange = { from: number; to: number };

interface HTMLDivElementWithSlider extends HTMLDivElement {
  noUiSlider: ReturnType<typeof noUiSlider.create>;
}
interface StyledSliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  values?: number[];
  onChange: (value: number) => void;
  unit?: string;
  label?: string;
  effectiveRanges?: EffectiveRange[];
  optimalRange?: EffectiveRange;
}

const StyledSlider: React.FC<StyledSliderProps> = ({
  value,
  min,
  max,
  step = 1,
  onChange,
  unit,
  values,
  effectiveRanges = [],
  optimalRange,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vals=values??Array.from({ length: Math.floor((max - min) / 10) + 1 }, (_, i) => min + i * 10).filter(v => v <= max);
    if (!sliderRef.current) return;

    if ((sliderRef.current as HTMLDivElementWithSlider).noUiSlider) {
      (sliderRef.current as HTMLDivElementWithSlider).noUiSlider.destroy();
    }
    const slider = noUiSlider.create(sliderRef.current, {
      start: [value],
      connect: [true, false],
      range: { min, max },
      step,

      // animate: false,
      behaviour: 'tap-drag',
      pips: {
        mode: 'values' as any,
        values: vals as any,
        density: 2,
      },
    });

    const tooltipEl = document.createElement('div');
    tooltipEl.className = 'noUi-tooltip custom-tooltip';
    tooltipEl.style.display = 'none';
    sliderRef.current.querySelector('.noUi-handle')?.appendChild(tooltipEl);

    slider.on('update', (values, handle) => {
      tooltipEl.textContent = `${Math.round(Number(values[handle]))}${unit ? ` ${unit}` : ''}`;
    });

    slider.on('start', () => {
      tooltipEl.style.display = 'block';
    });
    slider.on('end', () => {
      tooltipEl.style.display = 'none';
    });
    sliderRef.current.addEventListener('mouseenter', () => {
      tooltipEl.style.display = 'block';
    });
    sliderRef.current.addEventListener('mouseleave', () => {
      tooltipEl.style.display = 'none';
    });

    const debouncedOnChange = debounce((newValue: number) => {
      if (newValue !== value) {
        onChange(newValue);
      }
    }, 100); // 100 мс задержка

    slider.on('update', (values) => {
      const newValue = Number(values[0]);
      debouncedOnChange(newValue);
    });

    return () => {
      slider.destroy();
      debouncedOnChange.cancel();
    };
  }, []);

  useEffect(() => {
    if ((sliderRef.current as HTMLDivElementWithSlider)?.noUiSlider) {
      (sliderRef.current as HTMLDivElementWithSlider).noUiSlider.set(value);
    }
  }, [value]);

  return (
    <div className="styled-slider-wrapper">
      <div className="styled-slider-bar-container" style={{ position: 'relative' }}>
        {effectiveRanges.map((range, index) => {
          const left = ((range.from - min) / (max - min)) * 100;
          const width = ((range.to - range.from) / (max - min)) * 100;
          return (
            <div
              key={index}
              className="slider-effective-range"
              style={{ left: `${left}%`, width: `${width}%` }}
            />
          );
        })}
        {optimalRange && (
          <div
            className="slider-optimal-range"
            style={{
              left: `${((optimalRange.from - min) / (max - min)) * 100}%`,
              width: `${((optimalRange.to - optimalRange.from) / (max - min)) * 100}%`,
            }}
          />
        )}
        <div ref={sliderRef} className="styled-slider" key={`${min}-${max}-${step}`} />
      </div>
    </div>
  );
};

export default StyledSlider;